/* global runtime */
// Lønnskjøring: bygging av lønnsposter/posteringer, samt reiseregning og kjøregodtgjørelse.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.

const payrollStatusToUi = (status) => ({
  draft: 'utkast',
  approved: 'godkjent',
  paid: 'utbetalt',
  reversed: 'annullert',
}[status] || status || 'utkast');

const payrollUiStatusLabel = (status) => ({
  draft: t('pay.status.draft'),
  approved: t('pay.status.approved'),
  paid: t('pay.paidOut'),
  reversed: t('pay.status.annulled'),
}[status] || status || t('pay.status.draft'));

const payrollTermForDate = (iso) => {
  const month = Number(String(iso || '').slice(5, 7)) || 1;
  return Math.min(6, Math.max(1, Math.ceil(month / 2)));
};

const payrollYearFromValue = (value, fallback = new Date().getFullYear()) => {
  const year = Number(String(value || '').slice(0, 4));
  return Number.isFinite(year) && year > 1900 ? year : fallback;
};

const payrollRunPaymentYear = (run, fallback) =>
  payrollYearFromValue(run?.paymentDate, payrollYearFromValue(run?.period, fallback));

const payrollRunPeriodYear = (run, fallback) =>
  payrollYearFromValue(run?.period, payrollRunPaymentYear(run, fallback));

const payrollRunPeriodMonth = (run) => {
  const month = Number(String(run?.period || run?.paymentDate || '').slice(5, 7));
  return Number.isFinite(month) && month >= 1 && month <= 12 ? month : 1;
};

const isPostedPayrollRun = (run) => run?.status !== 'draft' && run?.status !== 'reversed';

const payrollPeriodLabel = (term) => {
  const labels = {
    1: `${t('pay.month.january')} - ${t('pay.month.february')}`,
    2: `${t('pay.month.march')} - ${t('pay.month.april')}`,
    3: `${t('pay.month.may')} - ${t('pay.month.june')}`,
    4: `${t('pay.month.july')} - ${t('pay.month.august')}`,
    5: `${t('pay.month.september')} - ${t('pay.month.october')}`,
    6: `${t('pay.month.november')} - ${t('pay.month.december')}`,
  };
  return labels[term] || '-';
};

const payrollDueLabel = (year, term) => {
  const due = {
    1: `${year}-03-15`,
    2: `${year}-05-15`,
    3: `${year}-07-15`,
    4: `${year}-09-15`,
    5: `${year}-11-15`,
    6: `${year + 1}-01-15`,
  }[term];
  return isoToNb(due);
};

const payrollTaxTermDefinitions = (dueYear) => [
  { term: 6, startMonth: 11, endMonth: 12, runsYear: dueYear - 1, due: `${dueYear}-01-15` },
  { term: 1, startMonth: 1, endMonth: 2, runsYear: dueYear, due: `${dueYear}-03-15` },
  { term: 2, startMonth: 3, endMonth: 4, runsYear: dueYear, due: `${dueYear}-05-15` },
  { term: 3, startMonth: 5, endMonth: 6, runsYear: dueYear, due: `${dueYear}-07-15` },
  { term: 4, startMonth: 7, endMonth: 8, runsYear: dueYear, due: `${dueYear}-09-15` },
  { term: 5, startMonth: 9, endMonth: 10, runsYear: dueYear, due: `${dueYear}-11-15` },
];
const activePayrollTaxPaymentsForYear = (state, year) =>
  safeArray(state.payrollTaxPaymentsByYear?.[String(year)]).filter((payment) => payment.status !== 'reversed');
const payrollTaxPaymentSettlementCode = (state, payment = {}) => {
  if (payment.settlementAccountCode) return String(payment.settlementAccountCode);
  const accountId = payment.accountId || payment.settlementAccountId;
  const account = safeArray(state.settings?.bankAccounts).find((entry) => entry.id === accountId);
  return account ? getLedgerAccountCode(account, '1920') : '1920';
};
const payrollTaxPaymentPostings = (state, payment = {}) => {
  if (payment.status === 'reversed') return [];
  const taxAmount = Math.max(0, num(payment.taxAmount));
  const agaAmount = Math.max(0, num(payment.agaAmount));
  const total = Math.round((taxAmount + agaAmount) * 100) / 100;
  if (total <= 0) return [];
  const desc = payment.note || `Betaling AGA/skatt ${payment.term}. termin`;
  const settlement = payrollTaxPaymentSettlementCode(state, payment);
  return [
    taxAmount > 0 && posting('2600', 'Forskuddstrekk ansatte', desc, taxAmount),
    agaAmount > 0 && posting('2770', 'Skyldig arbeidsgiveravgift', desc, agaAmount),
    posting(settlement, plumme1AccountName(settlement, 'Oppgj\u00f8rskonto'), desc, -total),
  ].filter(Boolean);
};
const splitPayrollTaxPayment = ({ amount, openTax, openAga, allocationMode = 'proportional', taxAmount: manualTaxAmount, agaAmount: manualAgaAmount }) => {
  const totalOpen = Math.round((Math.max(0, num(openTax)) + Math.max(0, num(openAga))) * 100) / 100;
  const paymentAmount = Math.round(num(amount) * 100) / 100;
  if (paymentAmount <= 0 || totalOpen <= 0) return { taxAmount: 0, agaAmount: 0 };
  if (allocationMode === 'manual') {
    return {
      taxAmount: Math.max(0, Math.round(num(manualTaxAmount) * 100) / 100),
      agaAmount: Math.max(0, Math.round(num(manualAgaAmount) * 100) / 100),
    };
  }
  if (allocationMode === 'tax-first') {
    const taxAmount = Math.min(Math.max(0, num(openTax)), paymentAmount);
    return { taxAmount, agaAmount: Math.round((paymentAmount - taxAmount) * 100) / 100 };
  }
  if (allocationMode === 'aga-first') {
    const agaAmount = Math.min(Math.max(0, num(openAga)), paymentAmount);
    return { taxAmount: Math.round((paymentAmount - agaAmount) * 100) / 100, agaAmount };
  }
  if (paymentAmount >= totalOpen) return { taxAmount: Math.max(0, num(openTax)), agaAmount: Math.max(0, num(openAga)) };
  const taxAmount = Math.min(Math.max(0, num(openTax)), Math.round(paymentAmount * (Math.max(0, num(openTax)) / totalOpen) * 100) / 100);
  return { taxAmount, agaAmount: Math.round((paymentAmount - taxAmount) * 100) / 100 };
};

const payrollPayTypes = [
  { name: 'Fastlønn', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true },
  { name: 'Timelønn', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true },
  { name: 'Overtid', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true },
  { name: 'Kilometergodtgjørelse', kind: 'Trekkfri godtgjørelse', aga: false, tax: false, holiday: false, pension: false, active: true },
  { name: 'Feriepenger', kind: 'Lønnstype', aga: true, tax: false, holiday: false, pension: false, active: true },
];

const payrollStandardPayTypes = [
  { id: 'monthly-salary', payType: 'monthly-salary', name: 'Fastlønn', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'hourly', payType: 'hourly', name: 'Timelønn - ikke overtid', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'overtime-hourly', payType: 'overtime-hourly', name: 'Timelønn - overtid', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'commission-pay', payType: 'commission-pay', name: 'Honorar, akkord, prosent eller provisjonslønn', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'bonus-holiday-pay', payType: 'bonus-holiday-pay', name: 'Bonus, feriepengeopptjenende', kind: 'Lønnstype', aga: true, tax: true, holiday: true, pension: true, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'bonus-no-holiday-pay', payType: 'bonus-no-holiday-pay', name: 'Bonus, ikke feriepengeopptjenende', kind: 'Lønnstype', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'board-fee', payType: 'board-fee', name: 'Styrehonorar', kind: 'Lønnstype', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '2930', accountToCode: '5000' },
  { id: 'company-car', payType: 'company-car', name: 'Fri bil', kind: 'Naturalytelse', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '5290', accountToCode: '5200' },
  { id: 'other-benefits', payType: 'other-benefits', name: 'Andre fordeler', kind: 'Naturalytelse', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '5290', accountToCode: '5280' },
  { id: 'taxable-insurance', payType: 'taxable-insurance', name: 'Forsikringer, skattepliktig del', kind: 'Naturalytelse', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '5290', accountToCode: '5251' },
  { id: 'car-km-allowance', payType: 'car-km-allowance', name: 'Kilometergodtgjørelse', kind: 'Trekkfri godtgjørelse', aga: false, tax: false, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '7100', accountToCode: '2911' },
  { id: 'car-km-allowance-taxable', payType: 'car-km-allowance-taxable', name: 'Kilometergodtgjørelse, trekkpliktig del', kind: 'Lønnstype', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '2930', accountToCode: '7160' },
  { id: 'holiday-pay-current', payType: 'holiday-pay-current', name: 'Feriepenger opptjent inneværende år', kind: 'Lønnstype', aga: true, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '2941', accountToCode: '5092' },
  { id: 'holiday-pay-previous', payType: 'holiday-pay-previous', name: 'Feriepenger opptjent tidligere år', kind: 'Lønnstype', aga: true, tax: false, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '2941', accountToCode: '5092' },
  { id: 'pension-premium', payType: 'pension-premium', name: 'Pensjonspremie OTP', kind: 'Trekk', aga: false, tax: false, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '5950', accountToCode: '2960' },
  { id: 'canteen-deduction', payType: 'canteen-deduction', name: 'Kantinetrekk', kind: 'Trekk', aga: false, tax: false, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '5919', accountToCode: '2930' },
  { id: 'holiday-deduction', payType: 'holiday-deduction', name: 'Trekk i lønn for ferie', kind: 'Trekk', aga: true, tax: true, holiday: true, pension: false, active: true, systemType: true, accountFromCode: '5000', accountToCode: '2930' },
  { id: 'extra-tax-deduction', payType: 'extra-tax-deduction', name: 'Ekstra skattetrekk', kind: 'Trekk', aga: false, tax: true, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '2600', accountToCode: '2930' },
];

const payrollTravelPayTypes = [
  { id: 'employee-expense-settlement', payType: 'employee-expense-settlement', name: 'Utleggsrefusjon reise', kind: 'Trekkfri godtgjørelse', aga: false, tax: false, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '7140', accountToCode: '2911' },
  { id: 'overnight-diet-hotel', payType: 'overnight-diet-hotel', name: 'Diett ved overnatting', kind: 'Trekkfri godtgjørelse', aga: false, tax: false, holiday: false, pension: false, active: true, systemType: true, accountFromCode: '7150', accountToCode: '2911' },
];

const payrollGeneratedPayTypes = new Set([
  'car-km-allowance', 'car-km-allowance-taxable', 'holiday-pay-current', 'holiday-pay-previous', 'pension-premium',
  'employee-expense-settlement', 'overnight-diet-hotel',
]);
const payrollCommonPayTypes = new Set([
  'hourly', 'monthly-salary', 'overtime-hourly', 'bonus-holiday-pay', 'commission-pay',
]);
const enrichPayrollPayTypeUsage = (entry = {}) => {
  const id = entry.payType || entry.id;
  const generated = payrollGeneratedPayTypes.has(id);
  return {
    ...entry,
    manualEntry: entry.manualEntry ?? !generated,
    fixedLine: entry.fixedLine ?? !generated,
    generatedByWorkflow: entry.generatedByWorkflow ?? generated,
    featured: entry.featured ?? payrollCommonPayTypes.has(id),
  };
};

const payrollPayTypeId = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 64);

const normalizePayrollPayTypeKind = (kind) => {
  const text = String(kind || '').toLowerCase();
  if (text.includes('natural')) return 'Naturalytelse';
  if (text.includes('trekkfri')) return 'Trekkfri godtgjørelse';
  if (text.includes('trekk')) return 'Trekk';
  return 'Lønnstype';
};

const normalizePayrollPayType = (entry = {}, existing = {}) => {
  const name = normalizePayrollText(entry.name || existing.name || '');
  if (!name) throw new Error(t('svc.nameMissing'));
  const id = entry.id || existing.id || entry.payType || existing.payType || `custom-${payrollPayTypeId(name) || createId('pay-type')}`;
  const kind = normalizePayrollPayTypeKind(entry.kind || existing.kind);
  const aga = entry.aga ?? entry.employerContribution ?? existing.aga ?? existing.employerContribution ?? kind === 'Lønnstype';
  const tax = entry.tax ?? entry.taxable ?? entry.taxDeduction ?? existing.tax ?? existing.taxable ?? existing.taxDeduction ?? kind !== 'Trekk';
  const holiday = entry.holiday ?? entry.holidayPay ?? existing.holiday ?? existing.holidayPay ?? kind === 'Lønnstype';
  const pension = entry.pension ?? entry.pensionBasis ?? existing.pension ?? existing.pensionBasis ?? kind === 'Lønnstype';
  return stripUndefinedDeep({
    ...existing,
    ...entry,
    id,
    payType: entry.payType || existing.payType || id,
    name,
    kind,
    aga: !!aga,
    tax: !!tax,
    holiday: !!holiday,
    pension: !!pension,
    active: entry.active ?? existing.active ?? true,
    accountFromCode: payrollCustomDeductionCreditAccount(id, kind, entry, existing),
    systemType: existing.systemType === true && entry.systemType !== false,
    updatedAt: nowIso(),
  });
};

const payrollPayTypesForSettings = (settings = {}) => {
  const byId = new Map([...payrollStandardPayTypes, ...payrollTravelPayTypes].map((entry) => [entry.id, { ...entry }]));
  safeArray(settings.customPayTypes).forEach((entry) => {
    try {
      const id = entry.id || entry.payType;
      const existing = id ? byId.get(id) : null;
      const normalized = normalizePayrollPayType(entry, existing || {});
      byId.set(normalized.id, normalized);
    } catch (error) {
      console.warn('Kunne ikke normalisere lønnstype', error);
    }
  });
  return [...byId.values()].map(enrichPayrollPayTypeUsage);
};

const getPayrollPayTypeDefinition = (type, settings = runtime.appState?.payrollSettings || {}) =>
  payrollPayTypesForSettings(settings).find((entry) => entry.payType === type || entry.id === type || entry.name === type);

const payrollReimbursementPayTypes = new Set(['car-km-allowance', 'employee-expense-settlement', 'overnight-diet-hotel', 'overnight-diet']);

const isPayrollReimbursementPayType = (payType, settings = runtime.appState?.payrollSettings || {}) => {
  const definition = getPayrollPayTypeDefinition(payType, settings);
  const kind = String(definition?.kind || '').toLowerCase();
  return payrollReimbursementPayTypes.has(payType) || kind.includes('trekkfri');
};

const fixedLineTypeLabel = (type) => getPayrollPayTypeDefinition(type)?.name || ({
  'monthly-salary': t('pay.fixedPay'),
  hourly: t('pay.hourlyPay'),
  'overtime-hourly': t('pay.overtimePay'),
  'car-km-allowance': t('pay.mileageAllowance'),
  'holiday-pay-current': t('pay.holidayPay'),
  'holiday-pay-previous': t('pay.holidayPay'),
  'pension-premium': t('pay.pension'),
}[type] || type || t('pay.salaryLine'));

const payrollLineTypeDefaults = (payType) => {
  const definition = getPayrollPayTypeDefinition(payType);
  if (definition) {
    return {
      label: definition.name,
      payout: definition.kind !== 'Trekk' && definition.kind !== 'Naturalytelse' && payType !== 'pension-premium',
      taxable: definition.tax,
      employerContribution: definition.aga,
      holidayPay: definition.holiday,
      pension: definition.pension,
    };
  }
  if (payType === 'car-km-allowance') {
    return { label: fixedLineTypeLabel(payType), payout: true, taxable: false, employerContribution: false, holidayPay: false, pension: false };
  }
  if (payType === 'holiday-pay-current' || payType === 'holiday-pay-previous') {
    return { label: fixedLineTypeLabel(payType), payout: true, taxable: false, employerContribution: true, holidayPay: false, pension: false };
  }
  if (payType === 'pension-premium') {
    return { label: fixedLineTypeLabel(payType), payout: false, taxable: false, employerContribution: false, holidayPay: false, pension: false };
  }
  return { label: fixedLineTypeLabel(payType), payout: true, taxable: true, employerContribution: true, holidayPay: true, pension: true };
};

const getPayrollPensionSettings = (settings = {}) => ({
  // OTP-satsen er bevisst opt-in — se functions/payroll/payrollCalculation.ts.
  percent: Math.max(0, num(settings.pensionContributionPercent, 2)),
  baseAmount: Math.max(0, payrollRateOrDefault(settings.pensionBaseAmount, 136549)),
  // Norge har hatt pensjonsopptjening fra første krone siden 2022. Overstyr
  // derfor gamle lagrede 1G-grenser i nye norske lønnskjøringer.
  lowerG: String(settings.country || '').toUpperCase() === 'NO' && String(settings.effectiveDate || '') >= '2022-01-01'
    ? 0
    : Math.max(0, payrollRateOrDefault(settings.pensionLowerG, 1)),
  upperG: Math.max(0, payrollRateOrDefault(settings.pensionUpperG, 12)),
});

const calculatePayrollPensionContribution = (grossSalary, settings = {}) => {
  const pension = getPayrollPensionSettings(settings);
  if (!pension.percent || !pension.baseAmount || grossSalary <= 0) return 0;
  const lowerMonthly = pension.baseAmount * pension.lowerG / 12;
  const upperMonthly = pension.baseAmount * pension.upperG / 12;
  const cappedSalary = upperMonthly > 0 ? Math.min(grossSalary, upperMonthly) : grossSalary;
  const eligibleSalary = Math.max(0, cappedSalary - lowerMonthly);
  return eligibleSalary * (pension.percent / 100);
};

const roundPayrollMoney = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;
const payrollPensionEligibleGross = (line, settings = {}) => safeArray(line?.items)
  .filter((item) => item?.payType !== 'pension-premium' && (item?.pension ?? payrollLineTypeDefaults(item?.payType).pension) !== false)
  .reduce((sum, item) => sum + num(item?.grossSalary), 0);

const payrollLinePensionContribution = (line, settings = {}) => {
  const manualPremium = safeArray(line?.items)
    .filter((item) => item?.payType === 'pension-premium')
    .reduce((sum, item) => sum + num(item?.amount), 0);
  if (manualPremium > 0) return manualPremium;
  if (safeArray(line?.items).length) return roundPayrollMoney(calculatePayrollPensionContribution(payrollPensionEligibleGross(line, settings), settings));
  const stored = num(line?.pensionContribution);
  if (stored > 0) return stored;
  if ((line?.pension ?? payrollLineTypeDefaults(line?.payType).pension) === false) return 0;
  return calculatePayrollPensionContribution(num(line?.grossSalary), settings);
};

// `num` gir 0 for manglende verdi, sa `num(x, 14.1)` gir aldri 14,1. Satser ma ha
// en reell default. Speiler rateOrDefault i functions/payroll/payrollCalculation.ts.
const payrollRateOrDefault = (value, fallback) => {
  if (value === undefined || value === null || String(value).trim() === '') return fallback;
  return num(value, fallback);
};

const payrollEmployerContributionRateFor = (employeeOrLine = {}, settings = {}) => {
  const standardRate = payrollRateOrDefault(settings.employerContributionRate, 14.1);
  // Configured legacy companies and old Norwegian reset states use the visible company rate.
  const configuredCompanyRate = String(settings.employerContributionZone || settings.municipalityCode || '').trim() || settings.completed === true;
  const incompleteNorwegianReset = String(settings.country || '').trim().toUpperCase() === 'NO' && settings.completed === false && settings.useStandardEmployerContributionRate == null && settings.employerContributionRate == null && !String(settings.employerContributionZone || '').trim() && !String(settings.municipalityCode || '').trim();
  if (settings.useStandardEmployerContributionRate === true || configuredCompanyRate || incompleteNorwegianReset) return standardRate;
  return payrollRateOrDefault(employeeOrLine.employerContributionRate, standardRate);
};
const payrollHolidayPayRateFor = (employee = {}, settings = {}) => {
  const standardRate = payrollRateOrDefault(settings.holidayPayPercent, 10.2);
  if (employee.useStandardHolidayPayRate === true) return standardRate;
  return payrollRateOrDefault(employee.holidayPayRate, standardRate);
};

// Utledes i stedet for aa leses fra `deductionAmount`, slik at utkast lagret for
// trekkstotten kom inn ogsaa blir kontert riktig.
const payrollItemDeductionAmount = (item, payrollSettings = {}) => {
  const payType = item?.payType;
  const definition = getPayrollPayTypeDefinition(payType, payrollSettings);
  if (definition?.kind !== 'Trekk' || payType === 'pension-premium' || payType === 'holiday-deduction') return 0;
  if (item?.taxable ?? payrollLineTypeDefaults(payType).taxable) return 0;
  return Math.max(0, num(item?.amount));
};

const buildPayrollRunItem = ({ employee, line, paymentDate, sourceId, payrollSettings }) => {
  const payType = line.payType || 'monthly-salary';
  const defaults = payrollLineTypeDefaults(payType);
  const definition = getPayrollPayTypeDefinition(payType, payrollSettings);
  const amount = Math.max(0, num(line.amount));
  const holidayDeduction = payType === 'holiday-deduction';
  const grossSalary = holidayDeduction ? -amount : isPayrollReimbursementPayType(payType, payrollSettings) || payType === 'pension-premium' || definition?.kind === 'Trekk' ? 0 : amount;
  const taxable = line.taxable ?? defaults.taxable;
  const payout = definition?.kind === 'Naturalytelse' ? false : (line.payout ?? defaults.payout);
  const employerContribution = line.employerContribution ?? defaults.employerContribution;
  const holidayPay = line.holidayPay ?? defaults.holidayPay;
  const pension = line.pension ?? defaults.pension;
  // Se functions/payroll/payrollCalculation.ts — samme regel begge steder.
  const deductionType = definition?.kind === 'Trekk' && payType !== 'pension-premium' && !holidayDeduction;
  const extraTaxDeduction = deductionType && taxable ? amount : 0;
  const deductionAmount = deductionType && !taxable ? amount : 0;
  const taxDeduction = (taxable ? grossSalary * (payrollEmployeeTaxRate(employee, paymentDate, payrollSettings?.runTaxDeductionMode) / 100) : 0) + extraTaxDeduction;
  const employerContributionAmount = employerContribution ? roundPayrollMoney(grossSalary * (payrollEmployerContributionRateFor(employee, payrollSettings) / 100)) : 0;
  const holidayPayAmount = holidayPay ? roundPayrollMoney(grossSalary * (payrollHolidayPayRateFor(employee, payrollSettings) / 100)) : 0;
  const pensionContribution = payType === 'pension-premium'
    ? amount
    : pension ? calculatePayrollPensionContribution(grossSalary, payrollSettings) : 0;
  return stripUndefinedDeep({
    id: line.id || sourceId || createId('payroll-item'),
    label: line.label || line.employmentLabel || defaults.label,
    payType,
    quantity: line.quantity,
    rate: line.rate,
    amount,
    grossSalary,
    payout,
    taxable,
    employerContribution,
    holidayPay,
    pension,
    taxDeduction,
    deductionAmount,
    employerContributionAmount,
    holidayPayAmount,
    pensionContribution,
    payoutAmount: payout ? Math.max(0, amount - taxDeduction) : 0,
    payTypeSnapshot: definition ? {
      id: definition.id || definition.payType,
      name: definition.name,
      kind: definition.kind,
      payout, taxable, employerContribution, holidayPay, pension,
    } : undefined,
    calculationRuleOverride: line.calculationRuleOverride === true || undefined,
    calculationRuleOverrideReason: line.calculationRuleOverrideReason || undefined,
    sourceWorkflow: line.sourceWorkflow || undefined,
    sourceId,
    // Bare refusjoner baerer kontering: kostnadskonto fra utleggslinjen og
    // dokumentert inngaaende mva som posteringen skal skille ut.
    costAccountCode: isPayrollReimbursementPayType(payType, payrollSettings) ? (line.costAccountCode || undefined) : undefined,
    deductibleVatAmount: isPayrollReimbursementPayType(payType, payrollSettings) && num(line.deductibleVatAmount) > 0
      ? Math.min(num(line.deductibleVatAmount), amount) : undefined,
    createdAt: line.createdAt || nowIso(),
  });
};

// Inngaaende mva paa et privat utlegg kan bare fradragsfoeres naar linjen
// faktisk baerer dokumentert mva: standard mva-behandling, et positivt belop og
// et bilag aa vise til. Mval. kap. 8 (fradrag) og bokforingsforskriften
// (dokumentasjon) — kilder NO-LOVDATA-VAT-ACT og NO-LOVDATA-BOOKKEEPING-REGULATION.
// Alt annet bokfores brutto som for; vi gjetter aldri en mva av et belop.
const travelLineDeductibleVat = (line = {}) => {
  const gross = num(line.amount);
  const vatRate = num(line.vatRate);
  // `num` sin fallback aktiveres ikke for manglende verdi (backend-kontrakten
  // sier tom verdi er 0), saa nett og mva utledes eksplisitt av sats og brutto.
  const explicitNet = num(line.amountNet);
  const amountNet = explicitNet > 0 ? explicitNet : (vatRate > 0 ? gross / (1 + vatRate / 100) : gross);
  const explicitVat = num(line.vatAmount);
  const vat = explicitVat > 0 ? explicitVat : Math.max(0, gross - amountNet);
  if (vat <= 0) return 0;
  if ((line.vatTreatment || (vatRate > 0 ? 'standard' : 'zeroRated')) !== 'standard') return 0;
  const documented = line.hasAttachment === true || safeArray(line.attachments).length > 0
    || Boolean(line.attachmentId || line.attachmentDownloadUrl || line.attachmentStoragePath);
  return documented ? Math.min(vat, gross) : 0;
};

const buildPayrollReadyTravelLines = (state, paymentDate) => {
  // Fradraget forutsetter registrert avgiftspliktig virksomhet (mval. kap. 8).
  // Er foretaket ikke mva-registrert, bokfores utlegget brutto uansett hva
  // linjen dokumenterer.
  const vatDeductionAllowed = state?.settings?.vatRegistered !== false;
  return safeArray(state.employeePortalTravels)
    .filter(window.PlummePayrollTravelClaims.readyForNewRun(state)) // approved and not held by another run (H9)
    .flatMap((travel) => {
      const base = {
        sourceType: 'employee-portal-travel',
        sourceId: travel.id,
        employeeId: travel.employeeId,
        employeeName: travel.employeeName || '',
      };
      const richLines = [
        ...safeArray(travel.mileageLines).flatMap((line) => {
          const kilometers = num(line.kilometers) * (line.returnTrip ? 2 : 1);
          const mileageAmount = num(line.mileageAmount, Math.round(kilometers * num(line.ratePerKm, 3.5)));
          const taxFreeAmount = Math.min(mileageAmount, Math.round(kilometers * 3.5 * 100) / 100);
          const taxableAmount = Math.max(0, Math.round((mileageAmount - taxFreeAmount) * 100) / 100);
          return [
            taxFreeAmount > 0 && stripUndefinedDeep({
              ...base,
              id: `travel-mileage-${travel.id}-${line.id}`,
              date: line.date || travel.dateFrom || paymentDate,
              description: `${line.fromLocation || travel.fromLocation || t('pay.driveFallback')} - ${line.toLocation || travel.toLocation || travel.destination || ''}`.trim(),
              payType: 'car-km-allowance',
              amount: taxFreeAmount,
              kilometers,
            }),
            taxableAmount > 0 && stripUndefinedDeep({
              ...base,
              id: `travel-mileage-taxable-${travel.id}-${line.id}`,
              date: line.date || travel.dateFrom || paymentDate,
              description: `${t('pay.taxableMileagePortion')} ${line.fromLocation || travel.fromLocation || t('pay.driveFallback').toLowerCase()} - ${line.toLocation || travel.toLocation || travel.destination || ''}`.trim(),
              payType: 'car-km-allowance-taxable',
              amount: taxableAmount,
              kilometers,
            }),
            normalizeTravelPaymentMethod(line.tollPaymentMethod) === 'private' && num(line.tollAmount) > 0 && stripUndefinedDeep({
              ...base,
              id: `travel-toll-${travel.id}-${line.id}`,
              date: line.date || travel.dateFrom || paymentDate,
              description: `${t('pay.tolls')} ${travel.purpose || travel.destination || ''}`.trim(),
              payType: 'employee-expense-settlement',
              amount: num(line.tollAmount),
            }),
          ].filter(Boolean);
        }),
        ...safeArray(travel.expenseLines)
          // A foreign-currency line without a stated NOK amount is never paid as kroner.
          .filter((line) => normalizeTravelPaymentMethod(line.paymentMethod) === 'private' && payrollExpenseNokAmount(line) != null)
          .map((line) => stripUndefinedDeep({
            ...base,
            id: `travel-expense-${travel.id}-${line.id}`,
            date: line.date || travel.dateFrom || paymentDate,
            description: line.description || line.category || travel.purpose || t('pay.expenses'),
            payType: line.payTypeId || 'employee-expense-settlement',
            amount: payrollExpenseNokAmount(line),
            // Kostnaden skal paa linjens konto, ikke flatt paa 7140, og
            // dokumentert mva skal skilles ut som fradrag i posteringen.
            costAccountCode: line.costAccountCode || travelExpenseAccountCode(line.category),
            deductibleVatAmount: (vatDeductionAllowed && payrollExpenseCurrency(line) === 'NOK' && travelLineDeductibleVat(line)) || undefined,
          })),
        ...safeArray(travel.accommodationLines)
          .filter((line) => num(line.payrollAmount, num(line.allowanceAmount)) > 0)
          .map((line) => stripUndefinedDeep({
            ...base,
            id: `travel-accommodation-${travel.id}-${line.id}`,
            date: line.dateFrom || travel.dateFrom || paymentDate,
            description: line.description || t('pay.overnightStayFallback'),
            payType: line.payTypeId || 'overnight-diet-hotel',
            amount: num(line.payrollAmount, num(line.allowanceAmount)),
          })),
      ];
      if (richLines.length) return richLines;
      const kilometers = num(travel.kilometers);
      return [stripUndefinedDeep({
        ...base,
        id: `travel-${travel.id}`,
        date: travel.dateFrom || travel.createdAt || paymentDate,
        description: travel.purpose || travel.destination || t('pay.mileagePayoutFallback'),
        payType: 'car-km-allowance',
        amount: Math.round(kilometers * 3.5),
        kilometers,
      })];
    })
    .filter((line) => line.employeeId && line.amount > 0);
};

const buildPayrollRunLine = ({ employee, paymentDate, includeFixedLines, readyLines, payrollSettings }) => {
  const fixedLines = includeFixedLines ? safeArray(employee.fixedPayrollLines).filter((line) => num(line.amount) > 0) : [];
  const salaryFallback = includeFixedLines && !fixedLines.length && employee.payType === 'monthly'
    ? Math.round(num(employee.monthlySalary || employee.agreedAnnualSalary) / (employee.monthlySalary ? 1 : 12))
    : 0;
  const items = [
    ...fixedLines,
    ...(salaryFallback > 0 ? [{ id: createId('payroll-item'), payType: 'monthly-salary', employmentLabel: t('pay.fixedPay'), amount: salaryFallback }] : []),
    ...readyLines.map((line) => ({
      id: line.id,
      payType: line.payType,
      employmentLabel: line.description,
      amount: line.amount,
      taxable: line.taxable,
      employerContribution: line.employerContribution,
      holidayPay: line.holidayPay,
      sourceId: line.sourceId,
      costAccountCode: line.costAccountCode,
      deductibleVatAmount: line.deductibleVatAmount,
    })),
  ].map((line) => buildPayrollRunItem({ employee, line, paymentDate, sourceId: line.sourceId, payrollSettings }));
  // En aktiv ansatt skal fortsatt ligge i utkastet nar brukeren velger bort
  // forhåndsutfylte linjer. Da kan timer eller annen lønn legges inn manuelt
  // fra ansattkortet før kjøringen godkjennes.
  const grossSalary = items.reduce((sum, item) => sum + num(item.grossSalary), 0);
  const expenseReimbursement = items.filter((item) => isPayrollReimbursementPayType(item.payType, payrollSettings)).reduce((sum, item) => sum + num(item.amount), 0);
  const taxDeduction = Math.floor(Math.max(0, items.reduce((sum, item) => sum + num(item.taxDeduction), 0)));
  const deductions = items.reduce((sum, item) => sum + payrollItemDeductionAmount(item, payrollSettings), 0);
  const employerContribution = items.reduce((sum, item) => sum + num(item.employerContributionAmount), 0);
  const holidayPayAccrual = items.reduce((sum, item) => sum + num(item.holidayPayAmount), 0);
  const pensionContribution = payrollLinePensionContribution({ items, grossSalary }, payrollSettings);
  const cashPayBase = items
    .filter((item) => item.payout && !isPayrollReimbursementPayType(item.payType, payrollSettings))
    .filter((item) => getPayrollPayTypeDefinition(item.payType, payrollSettings)?.kind !== 'Trekk' && item.payType !== 'pension-premium')
    .reduce((sum, item) => sum + num(item.amount), 0) + items.filter((item) => item.payType === 'holiday-deduction').reduce((sum, item) => sum + num(item.grossSalary), 0);
  const holidayPayPayout = items
    .filter((item) => item.payType === 'holiday-pay-current' || item.payType === 'holiday-pay-previous')
    .reduce((sum, item) => sum + num(item.amount), 0);
  const holidayPayBase = items
    .filter((item) => item.holidayPay)
    .reduce((sum, item) => sum + num(item.grossSalary), 0);
  return stripUndefinedDeep({
    id: createId('payroll-line'),
    employeeId: employee.id,
    employeeName: employee.name || '-',
    netPayLiabilityAccountCode: payrollEmployeeLiabilityAccountCode(employee),
    expenseReimbursementLiabilityAccountCode: employeeExpenseLiabilityAccountCode(employee),
    holidayPayLiabilityAccountCode: holidayPayEmployeeLiabilityAccountCode(employee),
    hours: 0,
    items,
    grossSalary,
    taxDeduction,
    deductions,
    expenseReimbursement,
    postedExpenseReimbursement: expenseReimbursement,
    postedExpenseIds: readyLines.map((line) => line.sourceId),
    employerContribution,
    employerContributionRate: payrollEmployerContributionRateFor(employee, payrollSettings),
    employerContributionZone: employee.employerContributionZone,
    holidayPayAccrual,
    holidayPayBase,
    holidayPayPayout,
    pensionContribution,
    salaryAdvanceOffset: 0,
    netPay: Math.max(0, cashPayBase - taxDeduction - deductions) + expenseReimbursement,
  });
};

const buildPayrollRunFromState = (state, options = {}) => {
  const paymentDate = nbToIso(options.paymentDate || todayIso());
  const includeFixedLines = options.includeFixedLines !== false;
  const includeExpenseLines = options.includeExpenseLines !== false;
  const taxDeductionMode = normalizePayrollTaxDeductionMode(options.taxDeductionMode);
  const payrollSettings = {
    ...(state.payrollSettings || {}),
    country: state.settings?.country,
    effectiveDate: paymentDate,
    runTaxDeductionMode: taxDeductionMode,
  };
  const readyLines = includeExpenseLines ? buildPayrollReadyTravelLines(state, paymentDate) : [];
  // Mirrors buildPayrollRun in functions/payroll/payrollCalculation.ts.
  const period = String(options.period || paymentDate.slice(0, 7)).slice(0, 7);
  const activeIndex = new Map(safeArray(state.employees).filter((employee) => employee.active !== false).map((employee, index) => [employee, index]));
  const readyFor = (employee) => readyLines.filter((line) => line.employeeId === employee.id);
  const lines = safeArray(state.employees)
    .filter((employee) => payrollEmployedInPeriod(employee, period) || ((employee.active !== false || String(employee.employmentEndDate ?? '').trim()) && readyFor(employee).length > 0))
    .map((employee) => buildPayrollRunLine({
      employee: employee.payrollLiabilityAccountCode || employee.netPayLiabilityAccountCode || !activeIndex.has(employee)
        ? employee
        : { ...employee, payrollLiabilityAccountCode: `2930:${10001 + activeIndex.get(employee)}` },
      paymentDate,
      includeFixedLines: includeFixedLines && payrollEmployedInPeriod(employee, period),
      readyLines: readyFor(employee),
      payrollSettings,
    }))
    .filter(Boolean);
  if (!lines.length) return null;
  return stripUndefinedDeep({
    id: options.id || createId('payroll-run'),
    period,
    paymentDate,
    status: 'draft',
    taxDeductionMode,
    lines,
    note: options.note || undefined,
    createdAt: nowIso(),
  });
};

const normalizePayrollText = (value) => String(value ?? '')
  .replace(/L\u00c3\u00b8nn/g, 'L\u00f8nn')
  .replace(/P\u00c3\u00a5l\u00c3\u00b8pte/g, 'P\u00e5l\u00f8pte')
  .replace(/l\u00c3\u00b8nn/g, 'l\u00f8nn');

const payrollEmployeeLiabilitySuffix = (employeeOrLine = {}) => {
  const explicit = String(
    employeeOrLine.payrollLiabilityAccountSuffix ||
    employeeOrLine.employeeNumber ||
    employeeOrLine.employeeNo ||
    employeeOrLine.personnelNumber ||
    ''
  ).replace(/\D/g, '');
  if (explicit) return explicit.padStart(5, '0').slice(-5);
  const source = String(employeeOrLine.employeeId || employeeOrLine.id || employeeOrLine.employeeName || employeeOrLine.name || 'employee');
  let hash = 0;
  for (let index = 0; index < source.length; index += 1) hash = (Math.imul(hash, 31) + source.charCodeAt(index)) >>> 0;
  return String(10001 + (hash % 89999)).padStart(5, '0');
};
const payrollEmployeeLiabilityAccountCode = (employeeOrLine = {}) => {
  const explicit = String(
    employeeOrLine.netPayLiabilityAccountCode ||
    employeeOrLine.payrollLiabilityAccountCode ||
    employeeOrLine.liabilityAccountCode ||
    ''
  ).trim();
  return explicit || `2930:${payrollEmployeeLiabilitySuffix(employeeOrLine)}`;
};
const employeeExpenseLiabilityAccountCode = (employeeOrLine = {}) => String(employeeOrLine.expenseReimbursementLiabilityAccountCode || '').trim() || `2911:${payrollEmployeeLiabilitySuffix(employeeOrLine)}`;
const holidayPayEmployeeLiabilityAccountCode = (employeeOrLine = {}) => {
  const explicit = String(employeeOrLine.holidayPayLiabilityAccountCode || '').trim();
  return explicit || `2941:${payrollEmployeeLiabilitySuffix(employeeOrLine)}`;
};

const buildPayrollPostingRows = (run, payrollSettings = runtime?.appState?.payrollSettings || {}) => {
  if (!run || run.status === 'draft' || run.status === 'reversed') return [];
  const lines = safeArray(run.lines);
  const expenseReimbursement = safeArray(run.lines).reduce((sum, line) => sum + num(line.expenseReimbursement), 0);
  const employeeControlRows = lines.map((line) => {
    const items = safeArray(line.items);
    const rate = payrollEmployerContributionRateFor(line, payrollSettings);
    const salaryAga = !items.length ? num(line.employerContribution) : items.reduce((itemSum, item) => itemSum + (
      item.employerContribution === false || item.payType === 'holiday-pay-current' || item.payType === 'holiday-pay-previous'
        ? 0
        : num(item.grossSalary) * rate / 100
    ), 0);
    const holidayPayoutAga = items.reduce((itemSum, item) => itemSum + (
      (item.payType === 'holiday-pay-current' || item.payType === 'holiday-pay-previous') && item.employerContribution !== false
        ? num(item.grossSalary) * rate / 100
        : 0
    ), 0);
    const holidayCurrentPayoutAga = items.reduce((itemSum, item) => itemSum + (
      item.payType === 'holiday-pay-current' && item.employerContribution !== false ? num(item.grossSalary) * rate / 100 : 0
    ), 0);
    const holidayPreviousPayoutAga = items.reduce((itemSum, item) => itemSum + (
      item.payType === 'holiday-pay-previous' && item.employerContribution !== false ? num(item.grossSalary) * rate / 100 : 0
    ), 0);
    const holidayPayout = items
      .filter((item) => item.payType === 'holiday-pay-current' || item.payType === 'holiday-pay-previous')
      .reduce((sum, item) => sum + num(item.amount), 0);
    return {
      employeeId: line.employeeId,
      employeeName: line.employeeName || 'Ansatt',
      withholding: num(line.taxDeduction),
      salaryAga,
      holidayPayoutAga,
      holidayCurrentPayoutAga,
      holidayPreviousPayoutAga,
      holidayAccrualAga: num(line.holidayPayAccrual) * rate / 100,
      holidayAccrual: num(line.holidayPayAccrual),
      holidayPayout,
      holidayAccountCode: payrollSettings.preserveLegacyHolidayPayAccount ? '2941' : holidayPayEmployeeLiabilityAccountCode(line),
    };
  });
  const salaryEmployerContribution = employeeControlRows.reduce((sum, row) => sum + row.salaryAga, 0);
  const holidayPayAccrual = safeArray(run.lines).reduce((sum, line) => sum + num(line.holidayPayAccrual), 0);
  const pensionContribution = safeArray(run.lines).reduce((sum, line) => sum + payrollLinePensionContribution(line, payrollSettings), 0);
  const holidayPayEmployerContribution = employeeControlRows.reduce((sum, row) => sum + row.holidayAccrualAga, 0);
  const netPayRows = lines.map((line) => ({ accountCode: payrollEmployeeLiabilityAccountCode(line), description: `Skyldig lønn - ${line.employeeName || 'Ansatt'}`, amount: Math.max(0, num(line.netPay) - num(line.expenseReimbursement)), employeeId: line.employeeId, employeeName: line.employeeName })).filter((line) => line.amount > 0);
  const employeeExpenseLiabilityRows = lines.map((line) => ({ accountCode: employeeExpenseLiabilityAccountCode(line), description: `Skyldige utlegg - ${line.employeeName || 'Ansatt'}`, amount: num(line.expenseReimbursement), employeeId: line.employeeId, employeeName: line.employeeName })).filter((line) => line.amount > 0);
  const reimbursementRows = new Map();
  // Documented input VAT is posted to 2710; the liability to the employee remains gross.
  let reimbursementVat = 0;
  safeArray(run.lines).forEach((line) => safeArray(line.items).forEach((item) => {
    if (!isPayrollReimbursementPayType(item.payType, payrollSettings)) return;
    const definition = getPayrollPayTypeDefinition(item.payType, payrollSettings);
    const fallbackAccount = definition?.accountFromCode || (item.payType === 'car-km-allowance' ? '7100' : item.payType === 'overnight-diet' ? '7150' : '7140');
    const accountCode = item.costAccountCode || fallbackAccount;
    const vat = Math.max(0, Math.min(num(item.deductibleVatAmount), num(item.amount)));
    reimbursementVat += vat;
    const key = `${line.employeeId}:${accountCode}:${item.payType}`;
    const current = reimbursementRows.get(key) || { accountCode, description: definition?.name || item.label || 'Trekkfri refusjon', amount: 0, employeeId: line.employeeId, employeeName: line.employeeName, sourceIds: [] };
    current.amount += num(item.amount) - vat;
    if (item.sourceId) current.sourceIds.push(item.sourceId);
    reimbursementRows.set(key, current);
  }));
  const deductionRows = new Map();
  safeArray(run.lines).flatMap((line) => safeArray(line.items)).forEach((item) => {
    const amount = payrollItemDeductionAmount(item, payrollSettings);
    if (amount <= 0) return;
    const definition = getPayrollPayTypeDefinition(item.payType, payrollSettings);
    const accountCode = definition?.accountFromCode || '5919';
    const key = `${accountCode}:${item.payType}`;
    const current = deductionRows.get(key) || { accountCode, description: definition?.name || item.label || 'Trekk i lønn', amount: 0 };
    current.amount += amount;
    deductionRows.set(key, current);
  });
  const wageExpenseRows = new Map();
  const naturalBenefitRows = new Map();
  lines.forEach((line) => {
    const items = safeArray(line.items);
    if (!items.length) {
      const current = wageExpenseRows.get('5000') || { accountCode: '5000', description: 'Lønn', amount: 0 };
      current.amount += num(line.grossSalary);
      wageExpenseRows.set('5000', current);
      return;
    }
    items.forEach((item) => {
      const definition = getPayrollPayTypeDefinition(item.payType, payrollSettings);
      if (isPayrollReimbursementPayType(item.payType, payrollSettings) || (definition?.kind === 'Trekk' && item.payType !== 'holiday-deduction') || item.payType === 'pension-premium') return;
      if (item.payType === 'holiday-pay-current' || item.payType === 'holiday-pay-previous') return;
      if (definition?.kind === 'Naturalytelse') {
        const key = `${definition.accountToCode || '5200'}:${definition.accountFromCode || '5290'}`;
        const current = naturalBenefitRows.get(key) || {
          debitAccountCode: definition.accountToCode || '5200',
          creditAccountCode: definition.accountFromCode || '5290',
          description: definition.name || item.label || 'Naturalytelse',
          amount: 0,
        };
        current.amount += num(item.amount);
        naturalBenefitRows.set(key, current);
        return;
      }
      const accountCode = item.payType === 'holiday-deduction' ? (definition?.accountFromCode || '5000') : (definition?.accountToCode || '5000');
      const current = wageExpenseRows.get(accountCode) || { accountCode, description: definition?.name || item.label || 'Lønn', amount: 0 };
      current.amount += item.payType === 'holiday-deduction' ? num(item.grossSalary) : num(item.amount);
      wageExpenseRows.set(accountCode, current);
    });
  });
  return [
    ...[...wageExpenseRows.values()].filter((row) => Math.abs(row.amount) > 0.005).map((row) => ({ accountCode: row.accountCode, description: `${row.description} ${run.period}`, debit: Math.max(0, row.amount), credit: Math.max(0, -row.amount) })),
    ...[...naturalBenefitRows.values()].filter((row) => row.amount > 0).flatMap((row) => [
      { accountCode: row.debitAccountCode, description: `${row.description} ${run.period}`, debit: row.amount, credit: 0 },
      { accountCode: row.creditAccountCode, description: `Motkonto ${row.description.toLowerCase()} ${run.period}`, debit: 0, credit: row.amount },
    ]),
    ...employeeControlRows.filter((row) => row.holidayPayout > 0).map((row) => ({ accountCode: row.holidayAccountCode, description: `Utbetaling feriepenger - ${row.employeeName}`, debit: row.holidayPayout, credit: 0, employeeId: row.employeeId, employeeName: row.employeeName, payrollPeriod: run.period })),
    holidayPayAccrual > 0 && { accountCode: '5092', description: `P\u00e5l\u00f8pte feriepenger ${run.period}`, debit: holidayPayAccrual, credit: 0 },
    salaryEmployerContribution > 0 && { accountCode: '5400', description: `Arbeidsgiveravgift ${run.period}`, debit: salaryEmployerContribution, credit: 0 },
    holidayPayEmployerContribution > 0 && { accountCode: '5410', description: `Arbeidsgiveravgift av påløpte feriepenger ${run.period}`, debit: holidayPayEmployerContribution, credit: 0 },
    pensionContribution > 0 && { accountCode: '5950', description: `Obligatorisk tjenestepensjon ${run.period}`, debit: pensionContribution, credit: 0 },
    ...[...reimbursementRows.values()].filter((row) => row.amount > 0).map((row) => ({ accountCode: row.accountCode, description: `${row.description} - ${row.employeeName || 'Ansatt'} ${run.period}`, debit: row.amount, credit: 0, reimbursement: true, employeeId: row.employeeId, employeeName: row.employeeName, sourceIds: row.sourceIds })),
    reimbursementVat > 0 && { accountCode: '2710', description: `Inngående mva ansattutlegg ${run.period}`, debit: reimbursementVat, credit: 0, reimbursement: true },
    ...employeeControlRows.filter((row) => row.withholding > 0).map((row) => ({ accountCode: '2600', description: `Forskuddstrekk - ${row.employeeName}`, debit: 0, credit: row.withholding, employeeId: row.employeeId, employeeName: row.employeeName, payrollPeriod: run.period })),
    ...employeeControlRows.filter((row) => row.salaryAga + row.holidayPayoutAga > 0).map((row) => ({ accountCode: '2770', description: `Skyldig arbeidsgiveravgift - ${row.employeeName}`, debit: 0, credit: row.salaryAga + row.holidayPayoutAga, employeeId: row.employeeId, employeeName: row.employeeName, payrollPeriod: run.period })),
    ...employeeControlRows.filter((row) => row.holidayCurrentPayoutAga > 0).map((row) => ({ accountCode: '2780', description: `Reklassifisering AGA feriepenger inneværende år - ${row.employeeName}`, debit: row.holidayCurrentPayoutAga, credit: 0, employeeId: row.employeeId, employeeName: row.employeeName, accrualYear: Number(String(run.paymentDate || run.period || '').slice(0, 4)), payrollPeriod: run.period })),
    ...employeeControlRows.filter((row) => row.holidayPreviousPayoutAga > 0).map((row) => ({ accountCode: '2780', description: `Reklassifisering AGA feriepenger tidligere år - ${row.employeeName}`, debit: row.holidayPreviousPayoutAga, credit: 0, employeeId: row.employeeId, employeeName: row.employeeName, accrualYear: Number(String(run.paymentDate || run.period || '').slice(0, 4)) - 1, payrollPeriod: run.period })),
    ...employeeControlRows.filter((row) => row.holidayAccrualAga > 0).map((row) => ({ accountCode: '2780', description: `Påløpt AGA feriepenger - ${row.employeeName}`, debit: 0, credit: row.holidayAccrualAga, employeeId: row.employeeId, employeeName: row.employeeName, accrualYear: Number(String(run.paymentDate || run.period || '').slice(0, 4)), payrollPeriod: run.period })),
    ...employeeControlRows.filter((row) => row.holidayAccrual > 0).map((row) => ({ accountCode: row.holidayAccountCode, description: `Skyldige feriepenger - ${row.employeeName}`, debit: 0, credit: row.holidayAccrual, employeeId: row.employeeId, employeeName: row.employeeName, accrualYear: Number(String(run.paymentDate || run.period || '').slice(0, 4)), payrollPeriod: run.period })),
    ...netPayRows.map((row) => ({ accountCode: row.accountCode, description: row.description, debit: 0, credit: row.amount, employeeId: row.employeeId, employeeName: row.employeeName, payrollPeriod: run.period })),
    ...employeeExpenseLiabilityRows.map((row) => ({ accountCode: row.accountCode, description: row.description, debit: 0, credit: row.amount, reimbursement: true, employeeId: row.employeeId, employeeName: row.employeeName, payrollPeriod: run.period })),
    pensionContribution > 0 && { accountCode: '2960', description: 'Skyldig OTP', debit: 0, credit: pensionContribution },
    ...[...deductionRows.values()].filter((row) => row.amount > 0).map((row) => ({ accountCode: row.accountCode, description: `${row.description} ${run.period}`, debit: 0, credit: row.amount })),
  ].filter(Boolean).map((posting) => ({ ...posting, description: normalizePayrollText(posting.description) }));
};

const travelExpenseAccountCode = (category = '') => {
  if (category === 'Programvare') return '6540';
  if (['Internett', 'Telefon', 'Kontorrekvisita', 'Aviser, tidsskrifter og b\u00f8ker'].includes(category)) return '6995';
  if (['Representasjon (m\u00e5ltid med eksterne)', 'Sosiale kostnader (julebord, hyttetur o.l.)'].includes(category)) return '7350';
  if (['Overtidsmat', 'Mat og drikke (p\u00e5 reise)', 'Overnatting'].includes(category)) return '7165';
  if (category === 'Drivstoff til leiebil (bensin, diesel, lading)') return '7000';
  if (category === 'Kurs og faglig oppdatering') return '7700';
  return '7140';
};

const normalizeTravelPaymentMethod = (value) => {
  const raw = String(value || '').toLowerCase();
  return raw.includes('firmakort') || raw.includes('company') ? 'company-card' : 'private';
};

const travelPaymentLabel = (value) => normalizeTravelPaymentMethod(value) === 'private' ? t('pay.privateExpense') : t('pay.companyCard');

const normalizePortalTravelMileageLine = (line = {}) => {
  const kilometers = num(line.kilometers ?? line.km);
  const returnTrip = line.returnTrip === true || line.sameReturn === true;
  const effectiveKilometers = kilometers * (returnTrip ? 2 : 1);
  const rate = num(line.ratePerKm ?? line.rate, 3.5);
  const tollAmount = num(line.tollAmount ?? line.tolls);
  const mileageAmount = num(line.mileageAmount ?? line.amount, Math.round(effectiveKilometers * rate * 100) / 100);
  const tollPaymentMethod = normalizeTravelPaymentMethod(line.tollPaymentMethod ?? line.tollsPaidBy);
  return stripUndefinedDeep({
    id: line.id || createId('travel-km'), ...(line.workflowVersion === 2 ? { workflowVersion: 2, ratePolicy: line.ratePolicy || 'tax-free', sourceMileageId: line.sourceMileageId || undefined, vehicleId: line.vehicleId || undefined } : {}),
    payTypeId: line.payTypeId || 'car-km-allowance',
    date: line.date || todayIso(),
    fromLocation: line.fromLocation || line.from || '',
    toLocation: line.toLocation || line.to || '',
    stops: safeArray(line.stops),
    kilometers,
    returnTrip,
    vehicle: line.vehicle || t('pay.ownCar'),
    ratePerKm: rate,
    tollAmount,
    tollPaymentMethod,
    tollsPaidBy: travelPaymentLabel(tollPaymentMethod),
    passengers: safeArray(line.passengers),
    trailerKilometers: num(line.trailerKilometers),
    forestRoadKilometers: num(line.forestRoadKilometers),
    mileageAmount,
    payrollAmount: mileageAmount + (tollPaymentMethod === 'private' ? tollAmount : 0),
    createdAt: line.createdAt || nowIso(),
    status: line.status || 'draft',
  });
};

const normalizePortalTravelExpenseLine = (line = {}, country = currentCountry()) => {
  const amount = num(line.amount);
  const vatRate = num(line.vatRate);
  const amountNet = num(line.amountNet, vatRate > 0 ? amount / (1 + vatRate / 100) : amount);
  const vatAmount = num(line.vatAmount, Math.max(0, amount - amountNet));
  const paymentMethod = normalizeTravelPaymentMethod(line.paymentMethod);
  return stripUndefinedDeep({
    id: line.id || createId('travel-exp'),
    payTypeId: line.payTypeId || 'employee-expense-settlement',
    category: line.category || t('pay.expenseCategoryOther'),
    date: line.date || todayIso(),
    description: String(line.description || '').trim() || line.category || t('pay.expenses'),
    paymentMethod,
    paymentMethodLabel: travelPaymentLabel(paymentMethod),
    companyCardAccountId: line.companyCardAccountId || undefined,
    companyCardAccountName: line.companyCardAccountName || undefined,
    amount,
    amountNet,
    vatAmount,
    vatRate,
    vatTreatment: line.vatTreatment || (vatRate > 0 ? 'standard' : 'zeroRated'),
    costAccountCode: line.costAccountCode || travelExpenseAccountCode(line.category),
    currency: line.currency || 'NOK',
    amountNok: payrollExpenseCurrency(line) !== 'NOK' && num(line.amountNok) > 0 ? num(line.amountNok) : undefined,
    exchangeRate: payrollExpenseCurrency(line) !== 'NOK' && num(line.exchangeRate) > 0 ? num(line.exchangeRate) : undefined,
    attachments: safeArray(line.attachments),
    hasAttachment: line.hasAttachment === true || safeArray(line.attachments).length > 0,
    createdAt: line.createdAt || nowIso(),
    status: line.status || 'draft',
    country,
    // Et privat utlegg kan hore til et prosjekt og skal da med i
    // fakturagrunnlaget — uten a bli bokfort som kjop, siden refusjonen
    // allerede gar gjennom lonn.
    ...(window.PlummeProjectCostLink?.normalizeProjectCostFields(line) || {}),
  });
};

const normalizePortalTravelAccommodationLine = (line = {}) => {
  const allowanceAmount = num(line.allowanceAmount, num(line.payrollAmount, num(line.amount)));
  const paymentMethod = normalizeTravelPaymentMethod(line.paymentMethod);
  return stripUndefinedDeep({
    id: line.id || createId('travel-acc'),
    payTypeId: line.payTypeId || 'overnight-diet-hotel',
    dateFrom: line.dateFrom || todayIso(),
    dateTo: line.dateTo || line.dateFrom || todayIso(),
    type: line.type || line.lodgingType || 'hotel',
    lodgingType: line.lodgingType || line.type || t('pay.hotelFallback'),
    location: line.location || undefined,
    description: line.description || line.location || t('pay.overnightStayFallback'),
    mealsPaidByDate: safeArray(line.mealsPaidByDate),
    meals: line.meals && typeof line.meals === 'object' ? line.meals : undefined,
    ratePerDay: num(line.ratePerDay),
    allowanceAmount,
    payrollAmount: num(line.payrollAmount, allowanceAmount),
    amount: num(line.amount, allowanceAmount),
    paymentMethod,
    paymentMethodLabel: travelPaymentLabel(paymentMethod),
    createdAt: line.createdAt || nowIso(),
    status: line.status || 'draft',
  });
};

const nextTravelNumber = (travels, year = new Date().getFullYear()) => {
  const max = safeArray(travels).reduce((current, travel) => {
    const raw = String(travel.number || travel.num || '');
    const match = raw.match(/(?:R-)?\d{4}-(\d+)/);
    return match ? Math.max(current, Number(match[1]) || 0) : current;
  }, 0);
  return `R-${year}-${String(max + 1).padStart(3, '0')}`;
};

// Endringslogg pa reiseregningen. Regnskapsforeren kan rette linjer for godkjenning,
// og da ma den ansatte kunne se at reisen ble endret etter innsending.
const TRAVEL_CHANGE_LOG_LIMIT = 50;
const currentActorName = () => String(
  runtime.userProfile?.displayName
  || runtime.userProfile?.name
  || runtime.user?.displayName
  || runtime.user?.email
  || '',
).trim();
const travelChangeEntry = ({ action = 'updated', actor = 'employer', actorName = '', note = '' } = {}) => stripUndefinedDeep({
  at: nowIso(),
  action,
  actor: actor === 'employee' ? 'employee' : 'employer',
  actorName: String(actorName || currentActorName() || '').trim() || undefined,
  note: String(note || '').trim() || undefined,
});
const appendTravelChangeLog = (existingLog, entry) => (entry
  ? [...safeArray(existingLog), entry].slice(-TRAVEL_CHANGE_LOG_LIMIT)
  : safeArray(existingLog));

const normalizeEmployeePortalTravel = (travel = {}, existing = {}, state = mergeState(runtime.appState)) => {
  const employee = safeArray(state.employees).find((entry) => entry.id === (travel.employeeId || existing.employeeId));
  if (!employee && !(travel.employeeName || existing.employeeName)) throw new Error(t('svc.pickEmployee'));
  const dateFrom = nbToIso(travel.dateFrom || existing.dateFrom || todayIso());
  const dateTo = nbToIso(travel.dateTo || existing.dateTo || dateFrom);
  const mileageLines = safeArray(travel.mileageLines ?? travel.mileage ?? existing.mileageLines)
    .map(normalizePortalTravelMileageLine);
  const expenseLines = safeArray(travel.expenseLines ?? travel.expenses ?? existing.expenseLines)
    .map((line) => normalizePortalTravelExpenseLine(line, state.settings?.country || currentCountry()));
  const accommodationLines = safeArray(travel.accommodationLines ?? existing.accommodationLines)
    .map(normalizePortalTravelAccommodationLine);
  const kilometers = mileageLines.reduce((sum, line) => sum + num(line.kilometers) * (line.returnTrip ? 2 : 1), 0);
  const firstMileage = mileageLines[0] || {};
  const createdAt = existing.createdAt || travel.createdAt || nowIso();
  return stripUndefinedDeep({
    ...existing,
    id: travel.id || existing.id || createId('travel'),
    number: travel.number || travel.num || existing.number || nextTravelNumber(state.employeePortalTravels, Number(dateFrom.slice(0, 4))),
    employeeId: travel.employeeId || existing.employeeId || employee.id,
    employeeName: travel.employeeName || travel.employee || existing.employeeName || employee.name,
    dateFrom,
    dateTo,
    destination: travel.destination || travel.country || existing.destination || firstMileage.toLocation || '',
    purpose: String(travel.purpose || existing.purpose || t('pay.travelPurposeFallback')).trim() || t('pay.travelPurposeFallback'),
    travelType: travel.travelType || existing.travelType || (mileageLines.length && !expenseLines.length ? 'mileage' : 'mixed'),
    kilometers: kilometers > 0 ? Number(kilometers.toFixed(1)) : undefined,
    fromLocation: travel.fromLocation || existing.fromLocation || firstMileage.fromLocation || undefined,
    toLocation: travel.toLocation || existing.toLocation || firstMileage.toLocation || undefined,
    hasDiet: Object.prototype.hasOwnProperty.call(travel, 'hasDiet') ? travel.hasDiet === true : existing.hasDiet === true || travel.diet === 'yes',
    overnight: Object.prototype.hasOwnProperty.call(travel, 'overnight') ? travel.overnight === true : existing.overnight === true,
    timeFrom: travel.timeFrom || existing.timeFrom || undefined,
    timeTo: travel.timeTo || existing.timeTo || undefined,
    coveredMeals: travel.coveredMeals || existing.coveredMeals || undefined,
    note: travel.note || existing.note || undefined,
    mileageLines,
    expenseLines,
    accommodationLines,
    status: travel.status || existing.status || 'draft',
    submittedByUid: existing.submittedByUid || travel.submittedByUid || undefined,
    submittedAt: existing.submittedAt || travel.submittedAt || createdAt,
    changeLog: safeArray(travel.changeLog ?? existing.changeLog).slice(-TRAVEL_CHANGE_LOG_LIMIT),
    createdAt,
    updatedAt: nowIso(),
  });
};

const mapPortalTravelToUi = (travel) => ({
  id: travel.id,
  num: travel.number || `R-${String(travel.createdAt || travel.submittedAt || '').slice(0, 4) || new Date().getFullYear()}-${String(travel.id || '').slice(-4).toUpperCase()}`,
  employeeId: travel.employeeId,
  employee: travel.employeeName || '-',
  purpose: travel.purpose || travel.destination || t('pay.travelPurposeFallback'),
  country: travel.destination || t('pay.norway'),
  dateFrom: travel.dateFrom || travel.createdAt || todayIso(),
  dateTo: travel.dateTo || travel.dateFrom || travel.createdAt || todayIso(),
  status: travel.status === 'approved' || travel.status === 'included-in-payroll' ? 'approved' : travel.status === 'rejected' ? 'rejected' : travel.status === 'draft' ? 'draft' : 'submitted',
  settled: travel.status === 'included-in-payroll',
  note: travel.note || '',
  rejectionNote: travel.rejectionNote || '',
  mileage: safeArray(travel.mileageLines).length ? safeArray(travel.mileageLines).map((line) => ({
    id: line.id,
    date: line.date || travel.dateFrom || todayIso(),
    from: line.fromLocation || '',
    to: line.toLocation || '',
    km: num(line.kilometers),
    returnTrip: line.returnTrip === true,
    vehicle: line.vehicle || t('pay.ownCar'),
    rate: num(line.ratePerKm, 3.5),
    tolls: num(line.tollAmount),
    tollsPaidBy: travelPaymentLabel(line.tollPaymentMethod),
    amount: num(line.mileageAmount),
    _backend: line,
  })) : (travel.kilometers ? [{
    id: `${travel.id}-km`,
    date: travel.dateFrom || todayIso(),
    from: travel.fromLocation || '',
    to: travel.toLocation || travel.destination || '',
    km: num(travel.kilometers),
    returnTrip: false,
    vehicle: t('pay.ownCar'),
    rate: 3.5,
    tolls: 0,
    tollsPaidBy: t('pay.privateExpense'),
    amount: Math.round(num(travel.kilometers) * 3.5),
  }] : []),
  expenses: safeArray(travel.expenseLines).map((line) => ({
    id: line.id,
    category: line.category || t('pay.expenses'),
    date: line.date || travel.dateFrom || todayIso(),
    description: line.description || '',
    paymentMethod: travelPaymentLabel(line.paymentMethod),
    amount: payrollExpenseNokAmount(line) ?? 0,
    hasAttachment: line.hasAttachment === true || safeArray(line.attachments).length > 0,
    _backend: line,
  })),
  accommodation: safeArray(travel.accommodationLines).map((line) => ({ ...line })),
  _backend: travel,
});

const derivePayroll = (state) => {
  const employees = safeArray(state.employees).map(mapEmployeeToUi);
  const activeYear = Number(state.activeYear) || new Date().getFullYear();
  const payrollRuns = safeArray(state.payrollRuns);
  const paymentYearRuns = payrollRuns.filter((run) => payrollRunPaymentYear(run, activeYear) === activeYear);
  const mapPayrollRunToUi = (run) => {
    const pensionSettings = { ...(state.payrollSettings || {}), country: state.settings?.country, effectiveDate: run.paymentDate || run.paidAt };
    const employeeById = new Map(safeArray(state.employees).map((employee) => [String(employee.id || ''), employee]));
    // Drafts are previews, not frozen accounting. Recalculate them for display
    // so a corrected company AGA setting is visible before approval without
    // mutating previously approved or paid payroll history.
    const displayRun = run.status === 'draft' ? {
      ...run,
      lines: safeArray(run.lines).map((line) => recalculatePayrollRunLine(
        employeeById.get(String(line.employeeId || '')) || line, line, run.paymentDate || todayIso(), pensionSettings,
      )),
    } : run;
    const gross = safeArray(displayRun.lines).reduce((sum, line) => sum + num(line.grossSalary), 0);
    const tax = safeArray(displayRun.lines).reduce((sum, line) => sum + num(line.taxDeduction), 0);
    const aga = safeArray(displayRun.lines).reduce((sum, line) => sum + num(line.employerContribution), 0);
    const net = safeArray(displayRun.lines).reduce((sum, line) => sum + num(line.netPay), 0);
    const pension = safeArray(displayRun.lines).reduce((sum, line) => sum + payrollLinePensionContribution(line, pensionSettings), 0);
    return {
      id: run.id,
      period: run.period,
      paymentDate: isoToNb(run.paymentDate),
      paymentDateIso: run.paymentDate,
      employees: safeArray(run.lines).length,
      gross: Math.round(gross),
      tax: Math.round(tax),
      aga: Math.round(aga),
      pension: Math.round(pension),
      net: Math.round(net),
      ...window.PlummePayrollObligations.runPaymentSummary(run), voucherNumber: run.voucherNumber || run.runNumber || '',
      status: payrollStatusToUi(run.status),
      _backend: displayRun,
    };
  };
  const runs = paymentYearRuns.map(mapPayrollRunToUi);
  const overview = employees.map((employee) => {
    const employeeRuns = paymentYearRuns.flatMap((run) => safeArray(run.lines).filter((line) => line.employeeId === employee.id));
    const gross = employeeRuns.reduce((sum, line) => sum + num(line.grossSalary), 0);
    const tax = employeeRuns.reduce((sum, line) => sum + num(line.taxDeduction), 0);
    const net = employeeRuns.reduce((sum, line) => sum + num(line.netPay), 0);
    const holiday = employeeRuns.reduce((sum, line) => sum + num(line.holidayPayAccrual), 0);
    const aga = employeeRuns.reduce((sum, line) => sum + num(line.employerContribution), 0);
    return { id: employee.id, name: employee.name, type: employee.lonnstype, gross, tax, net, holiday, aga, agaHoliday: Math.round(holiday * num(employee._backend?.employerContributionRate, 14.1) / 100), fte: num(employee._backend?.positionPercent, 100) / 100 };
  });
  const obligationModel = window.PlummePayrollObligations;
  const taxPayments = Object.values(state.payrollTaxPaymentsByYear || {}).flatMap((entries) => safeArray(entries)).filter((payment) => payment.status !== 'reversed');
  const paymentToUi = (payment) => ({
    id: payment.id,
    date: isoToNb(payment.date),
    dateIso: payment.date,
    amount: Math.round(num(payment.amount) * 100) / 100,
    taxAmount: Math.round(num(payment.taxAmount) * 100) / 100,
    agaAmount: Math.round(num(payment.agaAmount) * 100) / 100,
    allocationMode: payment.allocationMode || (payment.kind === 'withholding' ? 'tax-first' : 'aga-first'),
    kind: payment.kind,
    runId: payment.runId,
    reportingYear: payment.reportingYear,
    accountId: payment.accountId || '',
    settlementAccountCode: payrollTaxPaymentSettlementCode(state, payment),
    note: payment.note || '',
    _backend: payment,
  });
  const obligationRows = [
    ...obligationModel.withholdingObligations(state, activeYear),
    ...obligationModel.agaTermObligations(state, activeYear),
  ];
  const obligationMoney = (value) => Math.round(num(value) * 100) / 100;
  const terms = obligationRows.map((obligation) => {
    const termRuns = payrollRuns.filter((run) => {
      if (!isPostedPayrollRun(run)) return false;
      if (obligation.kind === 'withholding') return String(run.id) === String(obligation.runId);
      const salaryMatches = Number(String(run.paymentDate || '').slice(0, 4)) === activeYear && payrollTaxPaymentTerm({ date: run.paymentDate }) === Number(obligation.term);
      const pensionMatches = Number(String(run.paymentStatus?.pensionPaidAt || '').slice(0, 4)) === activeYear && payrollTaxPaymentTerm({ date: run.paymentStatus?.pensionPaidAt }) === Number(obligation.term);
      return salaryMatches || pensionMatches;
    });
    const payments = taxPayments
      .filter((payment) => obligation.kind === 'withholding'
        ? payment.kind === 'withholding' && String(payment.runId) === String(obligation.runId)
        : payment.kind === 'aga' && Number(payment.reportingYear) === activeYear && Number(payment.term) === Number(obligation.term))
      .map(paymentToUi)
      .sort((a, b) => String(a.dateIso || '').localeCompare(String(b.dateIso || '')));
    const tax = obligation.kind === 'withholding' ? obligation.amount : 0;
    const aga = obligation.kind === 'aga' ? obligation.amount : 0;
    const paidTax = payments.reduce((sum, payment) => sum + num(payment.taxAmount), 0);
    const paidAga = payments.reduce((sum, payment) => sum + num(payment.agaAmount), 0);
    const paid = paidTax + paidAga;
    const total = tax + aga;
    const openTax = Math.max(0, tax - paidTax);
    const openAga = Math.max(0, aga - paidAga);
    const employeeIds = new Set(termRuns.flatMap((run) => safeArray(run.lines).map((line) => line.employeeId).filter(Boolean)));
    return {
      id: obligation.id,
      kind: obligation.kind,
      runId: obligation.runId,
      reportingYear: obligation.reportingYear,
      term: obligation.term || payrollTaxPaymentTerm({ date: obligation.paymentDate }),
      period: obligation.kind === 'withholding' ? t('pay.taxTermWithholdingLabel', { date: isoToNb(obligation.paymentDate) }) : t('pay.taxTermAgaLabel', { term: obligation.term }),
      due: isoToNb(obligation.dueDate),
      dueIso: obligation.dueDate,
      runs: termRuns.length,
      employees: employeeIds.size,
      tax: obligationMoney(tax),
      aga: obligationMoney(aga),
      total: obligationMoney(total),
      paid: obligationMoney(paid),
      open: Math.max(0, obligationMoney(total - paid)),
      payments,
      paidTax: obligationMoney(paidTax),
      paidAga: obligationMoney(paidAga),
      openTax: Math.max(0, obligationMoney(openTax)),
      openAga: Math.max(0, obligationMoney(openAga)),
      status: total - paid <= 0.005 ? 'betalt' : paid > 0.005 ? 'delvis' : 'ikke',
      runIds: termRuns.map((run) => run.id),
    };
  }).sort((a, b) => String(a.dueIso || '').localeCompare(String(b.dueIso || '')));
  const holiday = payrollRuns.filter((run) => isPostedPayrollRun(run)).flatMap((run) => {
    const periodDate = `${String(run.period || '').slice(0, 7)}-01`;
    const paymentDate = nbToIso(run.paymentDate);
    return safeArray(run.lines).map((line) => {
      const earned = Math.max(0, num(line.holidayPayAccrual));
      const payout = Math.max(0, num(line.holidayPayPayout));
      const employeePaid = run.status === 'paid' || window.PlummePayrollObligations.employeePayment(run, line.employeeId).paymentStatus === 'paid';
      const paid = employeePaid ? payout : 0;
      const earnedInYear = earned > 0.005 && payrollYearFromValue(periodDate, activeYear) === activeYear;
      const paidInYear = paid > 0.005 && payrollYearFromValue(paymentDate, activeYear) === activeYear;
      return {
        id: `${run.id}-${line.id}-holiday`,
        employee: line.employeeName || employees.find((employee) => employee.id === line.employeeId)?.name || '-',
        period: run.period,
        periodDate,
        voucher: run.voucherNumber || '-',
        paymentDate: isoToNb(run.paymentDate),
        paymentDateIso: paymentDate,
        base: num(line.holidayPayBase, line.grossSalary),
        earned,
        paid,
        status: paid > 0.005 ? 'utbetalt' : payout > 0.005 ? 'holiday-unpaid' : 'opptjent',
        include: earnedInYear || paidInYear || (payout > 0.005 && payrollYearFromValue(paymentDate, activeYear) === activeYear),
      };
    });
  }).filter((row) => row.include).map((entry) => {
    const row = Object.assign({}, entry);
    delete row.include;
    return row;
  });
  const pension = paymentYearRuns.filter(isPostedPayrollRun).map(mapPayrollRunToUi).filter((run) => run.pension > 0).map((run) => {
    const paidAt = run._backend?.paymentStatus?.pensionPaidAt; const paidAmount = paidAt ? num(run._backend?.paymentStatus?.pensionPaidAmount) : 0;
    const premiumAmount = paidAt ? (run._backend?.paymentStatus?.pensionPremiumAmount == null ? paidAmount : num(run._backend.paymentStatus.pensionPremiumAmount)) : 0; return {
      id: `${run.id}-pension`, runId: run.id, period: run.period,
      voucher: run._backend?.voucherNumber || '-', employees: run.employees,
      amount: run.pension, calculatedAmount: run.pension, paidAmount, premiumAmount,
      feeAmount: paidAt ? num(run._backend?.paymentStatus?.pensionFeeAmount) : 0, overpaymentAmount: paidAt ? num(run._backend?.paymentStatus?.pensionOverpaymentAmount) : 0,
      paymentDifference: paidAt ? Math.round((premiumAmount - run.pension) * 100) / 100 : 0,
      status: paidAt ? 'betalt' : 'ikke', paidDate: isoToNb(paidAt) };
  });
  const fixedLines = employees.flatMap((employee) => safeArray(employee.fixedPayrollLines).map((line) => ({
    id: line.id,
    employeeId: employee.id,
    emp: employee.name,
    type: fixedLineTypeLabel(line.payType),
    payType: line.payType,
    amount: num(line.amount),
    note: line.comment || line.employmentLabel || '',
    _backend: line,
  })));
  return {
    employees,
    overview,
    runs,
    terms,
    holiday,
    pension,
    fixedLines,
    payTypes: payrollPayTypesForSettings(state.payrollSettings),
    readyLines: buildPayrollReadyTravelLines(state, todayIso()),
    travels: safeArray(state.employeePortalTravels).map(mapPortalTravelToUi),
  };
};

const getBankAccountLabel = (account) => {
  const name = account.name || (account.kind === 'tax_withholding' ? 'Skattetrekkskonto' : account.kind === 'savings' ? 'Sparekonto' : 'Driftskonto');
  const parts = [name, account.ledgerAccountCode || account.ledger || account.accountCode, account.accountNumber].filter(Boolean);
  return parts.join(' - ');
};

const mapBankAccountsToUi = (state) => {
  const sourceAccounts = safeArray(state.settings?.bankAccounts);
  const accounts = sourceAccounts
    .filter((account) => account.active !== false)
    .map((account) => ({
      id: account.id,
      label: getBankAccountLabel(account),
      source: account.kind === 'private' ? 'private' : 'business',
      // Kontoens valuta. NOK naar ingenting er satt — de aller fleste kontoer.
      currency: String(account.currency || 'NOK').toUpperCase(),
      _backend: account,
    }));
  if (accounts.length || sourceAccounts.length || !state.settings?.bankAccount) return accounts;
  return [{
    id: 'settings-bank-account',
    label: ['Driftskonto', '1920', state.settings.bankAccount].filter(Boolean).join(' - '),
    source: 'business',
    _backend: {
      id: 'settings-bank-account',
      name: 'Driftskonto',
      accountNumber: state.settings.bankAccount,
      ledgerAccountCode: '1920',
      kind: 'operating',
      reconcile: true,
    },
  }];
};

const getLedgerAccountCode = (account, fallback = '1920') =>
  String(account?.ledgerAccountCode || account?.ledger || account?.accountCode || fallback || '1920').trim();

const getPaymentSettlementDefaults = (state) => {
  const country = String(state?.settings?.country || 'NO').toUpperCase();
  if (country === 'SE') return { company: '1930', private: '2018', cash: '1910' };
  if (country === 'DK') return { company: '1920', private: '2050', cash: '1900' };
  return { company: '1920', private: '2063', cash: '1900' };
};

const normalizePaymentAccountLabel = (label) =>
  String(label || '').replace(/\s*(?:\u00c2\u00b7|\u00b7)\s*/g, ' - ').trim();

const mapPaymentAccountsToUi = (state) => {
  const defaults = getPaymentSettlementDefaults(state);
  const sourceBankAccounts = safeArray(state.settings?.bankAccounts);
  const activeSourceBankAccounts = sourceBankAccounts.filter((account) => account.active !== false);
  const bankAccounts = sourceBankAccounts
    .filter((account) => account.active !== false)
    .filter((account) => typeof account.showInPaymentChoices === 'boolean' ? account.showInPaymentChoices : (account.invoiceDefault || activeSourceBankAccounts.length === 1))
    .filter((account) => account.kind !== 'private')
    .map((account) => {
      const code = getLedgerAccountCode(account, defaults.company);
      return {
        id: account.id || code,
        value: code,
        label: getBankAccountLabel({ ...account, ledgerAccountCode: code }),
      };
    });
  const extraPaymentAccounts = safeArray(state.settings?.paymentAccounts)
    .filter((account) => account.active !== false)
    .map((account) => {
      const code = getLedgerAccountCode(account, account.kind === 'private' ? defaults.private : account.kind === 'cash' ? defaults.cash : defaults.company);
      return {
        id: account.id || code,
        value: code,
        label: getBankAccountLabel({ ...account, ledgerAccountCode: code }),
      };
    });
  const fallbackBank = !sourceBankAccounts.length && state.settings?.bankAccount
    ? [{
        id: 'settings-bank-account',
        value: '1920',
        label: ['Driftskonto', '1920', state.settings.bankAccount].filter(Boolean).join(' - '),
      }]
    : [];
  const base = bankAccounts.length ? bankAccounts : fallbackBank;
  const withCash = [
    ...base,
    ...extraPaymentAccounts,
    { id: 'cash-1900', value: '1900', label: 'Kontanter - 1900' },
  ];
  const withFallbacks = [
    ...withCash,
    { id: `private-${defaults.private}`, value: defaults.private, label: `Privat - ${defaults.private}` },
    { id: `cash-${defaults.cash}`, value: defaults.cash, label: `Kontanter - ${defaults.cash}` },
  ];
  return withFallbacks
    .filter((account) => account.id !== 'cash-1900' || defaults.cash === '1900')
    .map((account) => {
      const normalized = account.id === 'settings-bank-account'
        ? { ...account, value: defaults.company, label: ['Driftskonto', defaults.company, state.settings?.bankAccount].filter(Boolean).join(' - ') }
        : account;
      return { ...normalized, label: normalizePaymentAccountLabel(normalized.label) };
    })
    .filter((account, index, list) => list.findIndex((entry) => entry.value === account.value) === index);
};
